import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Target, Users, TrendingUp, Filter, Search, Download } from 'lucide-react';
import QuestionnaireLeaderboard from '../../components/Leaderboard/QuestionnaireLeaderboard';
import AccuracyLeaderboard from '../../components/Leaderboard/AccuracyLeaderboard';
import leaderboardService from '../../services/leaderboardService';

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('questionnaire');
  const [questionnaires, setQuestionnaires] = useState([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: 'all',
    userType: 'all',
    searchTerm: ''
  });

  useEffect(() => {
    loadQuestionnaires();
  }, []);

  useEffect(() => {
    // Parse URL parameters for tab and questionnaire selection
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') || 'questionnaire';
    const questionnaireId = params.get('questionnaireId');
    
    setActiveTab(tab);
    if (questionnaireId && questionnaires.length > 0) {
      const questionnaire = questionnaires.find(q => q.id === parseInt(questionnaireId));
      setSelectedQuestionnaire(questionnaire);
    }
  }, [location.search, questionnaires]);

  const loadQuestionnaires = async () => {
    try {
      setLoading(true);
      const data = await leaderboardService.getQuestionnairesForLeaderboard();
      setQuestionnaires(data);
      if (data.length > 0 && !selectedQuestionnaire) {
        setSelectedQuestionnaire(data[0]);
      }
    } catch (error) {
      console.error('Error loading questionnaires:', error);
      setError('Error al cargar los cuestionarios');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/leaderboard?tab=${tab}`, { replace: true });
  };

  const handleQuestionnaireChange = (questionnaire) => {
    setSelectedQuestionnaire(questionnaire);
    navigate(`/leaderboard?tab=questionnaire&questionnaireId=${questionnaire.id}`, { replace: true });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleExport = async () => {
    try {
      const format = 'csv';
      const questionnaireId = activeTab === 'questionnaire' && selectedQuestionnaire 
        ? selectedQuestionnaire.id 
        : null;
      
      const data = await leaderboardService.exportLeaderboardData(questionnaireId, format);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leaderboard-${activeTab}-${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 rounded-full border-4 border-green-aqua/20 border-t-green-aqua animate-spin"></div>
              <p className="text-lg font-medium text-gray-600">Cargando tabla de clasificación...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-2xl">!</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadQuestionnaires}
              className="px-4 py-2 bg-green-aqua text-white rounded-lg hover:bg-green-aqua/90 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Tabla de Clasificación
              </h1>
              <p className="text-gray-600">
                Compara tu rendimiento con otros usuarios en los cuestionarios
              </p>
            </div>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-green-aqua text-white rounded-lg hover:bg-green-aqua/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              Exportar
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => handleTabChange('questionnaire')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'questionnaire'
                    ? 'border-green-aqua text-green-aqua'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Puntuación por Cuestionario
                </div>
              </button>
              <button
                onClick={() => handleTabChange('accuracy')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'accuracy'
                    ? 'border-green-aqua text-green-aqua'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Precisión General
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Questionnaire Selector (only for questionnaire tab) */}
        {activeTab === 'questionnaire' && (
          <div className="mb-6">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">
                Seleccionar Cuestionario:
              </label>
              <select
                value={selectedQuestionnaire?.id || ''}
                onChange={(e) => {
                  const questionnaire = questionnaires.find(q => q.id === parseInt(e.target.value));
                  handleQuestionnaireChange(questionnaire);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-aqua focus:border-green-aqua"
              >
                {questionnaires.map((questionnaire) => (
                  <option key={questionnaire.id} value={questionnaire.id}>
                    {questionnaire.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {activeTab === 'questionnaire' ? (
            <QuestionnaireLeaderboard
              questionnaire={selectedQuestionnaire}
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          ) : (
            <AccuracyLeaderboard
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage; 